import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import DatepickerField from 'components/DatepickerField';
import validations from 'validations';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DATA_ROOM_SHAREABLE_LINK_EXPIRES,
  DataRoomShareableLink,
} from 'models/ShareableLink';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './ProlongShareableLinkModal.styles.scss';

type ProlongShareableLinkModalProps = {
  className?: string;
  shareableLinkId: string;
  linkExpiresDate: Date;
  onSuccess: (shareableLink: DataRoomShareableLink) => void;
  onClose?: () => void;
};

const ProlongShareableLinkModal: React.ForwardRefRenderFunction<
  ModalRef,
  ProlongShareableLinkModalProps
> = (props, ref) => {
  const { className, shareableLinkId, linkExpiresDate, onSuccess, onClose } =
    props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('prolong-shareable-link-modal', className);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async ({ linkExpiresDate }: { linkExpiresDate: Date }) => {
      const patchValues = {
        linkExpiresDate,
        linkExpires: DATA_ROOM_SHAREABLE_LINK_EXPIRES.OTHER,
      };

      try {
        const { data } = await dataRoomApi.shareableLinks.patchShareableLink(
          shareableLinkId,
          patchValues,
        );

        popSuccess(t('expirationLinkHasBeenSuccessfullyUpdated'));
        modal.close();

        onSuccess(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [shareableLinkId, modal, onSuccess, popSuccess, t, popServerError],
  );

  return (
    <Modal
      className={classes}
      ref={modalRef}
      title={t('linkExpiresAfter')}
      onClose={onClose}
    >
      {modal?.openState && (
        <Form
          onSubmit={onSubmit}
          initialValues={{ linkExpiresDate }}
          render={({ handleSubmit, submitting, pristine }) => (
            <form onSubmit={handleSubmit}>
              <Field
                name="linkExpiresDate"
                component={DatepickerField}
                onlyFuture
                required
                validate={validations.composeValidators(
                  validations.required(),
                  validations.onlyFuture(t('onlyFuture')),
                )}
              />

              <div className="actions">
                <Button variant="link" onClick={modal?.close}>
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={pristine || submitting}>
                  {t('confirm')}
                </Button>
              </div>
            </form>
          )}
        />
      )}
    </Modal>
  );
};

export default forwardRef(ProlongShareableLinkModal);
